import { useEffect, useRef, useState } from 'react';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
const hasSupport = typeof IntersectionObserver !== 'undefined';
/**
 *
 * Returns a `ref` and `boolean`. Detects if the element with the ref is inView and updates the value.
 *
 * 🚨 When you want to use an already available `ref` or have the need to use a performance-optimized `MotionValue` then the Framer [useInView](https://www.framer.com/docs/use-in-view/) could be a good alternative while it has a different use-case.
 *
 * ```js
 * import { useInView } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = () => {
 *    const [ref, isInView] = useInView<HTMLDivElement>({
 *      // See https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 *      root: null,
 *      rootMargin: '0 0 0 0',
 *      threshold: 0,
 *      // Only trigger from false -> true, once. Useful when you want to animate something in and keep it that way
 *      triggerOnce: false,
 *      // When optimising you might want to avoid a render, this makes sure the 2nd value returned is a MotionValue
 *      motionValue: false,
 *    });
 *
 *    return <div ref={ref}>...</div>
 * }
 * ```
 *
 * This hook is especially useful when working with many components (or where performance is key) while it can use a `MotionValue` to prevent unnecessary re-renders.
 * For example in the `ImageSequence` component to only adjust the style property and no React updates are triggered.
 *
 * ```js
 * import { useInView } from '@polestar/component-warehouse-react';
 * import { useSpring } from 'framer-motion';
 *
 * // This Component will animate in without triggering a rerender.
 * // When you have lots of components to animate this helps keeping the FPS at 60+
 * const MyComponent = () => {
 *    const [ref, isInView] = useInView<HTMLDivElement>({
 *      motionValue: true,
 *    });
 *
 *    const opacity = useTransform(isInView, (latestInView) => latestInView ? 1 : 0);
 *    const animatedOpacity = useSpring(opacity);
 *
 *    return <motion.div ref={ref} style={{
 *      opacity: animatedOpacity
 *    }}>...</div>
 * }
 * ```
 *
 * If you want to use the provided `ref` within forwardRef setup to listen in if it is in view then use `useCombinedRef`:
 *
 * ```js
 * import { useInView } from '@polestar/component-warehouse-react';
 * import { useSpring } from 'framer-motion';
 *
 * // This Component will animate in without triggering a rerender.
 * // When you have lots of components to animate this helps keeping the FPS at 60+
 * const MyComponent = forwardRef<HTMLDivElement, XXProps>(props,ref ) => {
 *    const [ref:inViewRef, isInView] = useInView<HTMLDivElement>();
 *    const combinedRef = useCombinedRef( ref, inViewRef);
 *
 *    // do something with `isInView`
 *
 *    return <motion.div ref={combinedRef}>...</div>
 * }
 * ```
 *
 *
 * @storybook Hooks/useInView
 *
 */
const useInView = (options) => {
    const ref = useRef(null);
    const { root, rootMargin, threshold = 0, triggerOnce = false, motionValue = false } = options;
    const observer = useRef(null);
    const triggered = useRef(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, forceUpdate] = useState({});
    const isIntersected = useMutableValue(false);
    useEffect(() => {
        if (!hasSupport) {
            isIntersected.set(true);
            if (!motionValue) {
                forceUpdate({});
            }
            return;
        }
        if (observer.current) {
            observer.current.disconnect();
        }
        // Don't observe if it's already triggered
        if (triggered.current && triggerOnce) {
            return;
        }
        observer.current = new window.IntersectionObserver(([entry]) => {
            isIntersected.set(!!(entry === null || entry === void 0 ? void 0 : entry.isIntersecting));
            if (!motionValue) {
                forceUpdate({});
            }
            if (!!(entry === null || entry === void 0 ? void 0 : entry.isIntersecting)) {
                triggered.current = true;
                if (triggerOnce && observer.current) {
                    observer.current.disconnect();
                    observer.current = null;
                }
            }
        }, {
            root,
            rootMargin,
            threshold,
        });
        if (ref.current) {
            observer.current.observe(ref.current);
        }
        const currentObserver = observer.current;
        return () => {
            if (currentObserver) {
                currentObserver.disconnect();
            }
        };
    }, [ref, root, rootMargin, observer, threshold, triggerOnce, isIntersected, motionValue]);
    return [ref, motionValue ? isIntersected : isIntersected.get()];
};
export default useInView;
